import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiChevronDown, FiHeart, FiLoader, FiMessageCircle, FiUserPlus, FiVolume2, FiVolumeX, FiX,
} from 'react-icons/fi';
import { cReels, cToggleLike, fullUrl } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import { formatTime } from '../../utils/media';
import { playSfx } from '../../utils/sfx';
import { claimPlayback, playClip, releasePlayback, useVideoSound } from '../../utils/videoSound';
import CommentList from '../../components/community/CommentList';
import FollowButton from '../../components/community/FollowButton';
import PostVideo from '../../components/community/PostVideo';
import ShareMenu from '../../components/community/ShareMenu';
import UserAvatar from '../../components/community/UserAvatar';
import { BLUE } from '../../components/community/brand';

/**
 * Reels — the TikTok-style viewer: **one video full screen**, snap scrolling to
 * the next clip, the author's profile in the corner (tap it to open the
 * profile), like / comment / share and a mute switch.
 *
 * Every uploaded video appears here (vertical 9:16 clips first, then landscape).
 * **Sound is ON by default** (shared preference `ms_reel_sound`, the same one the
 * feed and stories use); if the browser blocks sound autoplay the clip starts
 * muted and a “tap to unmute” pill is shown.
 */
export default function ReelsPage() {
  const { t, lang } = useLanguage();
  const { user, requireAuth } = useCommunity();
  const navigate = useNavigate();
  const [reels, setReels] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useVideoSound();   // shared with the feed + stories
  const muted = !soundOn;
  const [blocked, setBlocked] = useState(false);   // browser refused unmuted autoplay
  const [active, setActive] = useState(0);
  const [pausedId, setPausedId] = useState(null);   // clip the viewer stopped on purpose
  const [commentsFor, setCommentsFor] = useState(null);
  const videoRefs = useRef({});
  const pausedByUser = useRef({});                 // id → true when the viewer paused it
  const container = useRef(null);

  const load = useCallback((nextPage) => {
    setLoading(true);
    // the API returns every uploaded video: portrait Reels first, landscape next
    return cReels({ page: nextPage, limit: 6 })
      .then((res) => {
        const items = res?.items || [];
        setReels((prev) => (nextPage === 1 ? items : [...prev, ...items]));
        setTotal(res?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1); }, [load]);

  /** Play a clip, unmuted when the viewer wants sound (fallback to muted). */
  const startClip = useCallback(async (el, withSound) => {
    claimPlayback(el);
    const st = await playClip(el, withSound);
    if (st === 'blocked') setBlocked(true);
  }, []);

  // Play only the clip in the middle of the screen (and pause the others).
  // A clip the viewer paused stays paused (scroll away → it can play again).
  useEffect(() => {
    const nodes = Object.entries(videoRefs.current).map(([id, el]) => ({ id, el }));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const id = el.dataset.id;
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
          setActive(Number(id));
          if (!pausedByUser.current[id]) startClip(el, !muted);
        } else {
          el.pause();
          releasePlayback(el);                   // no longer owns the single playback slot
          delete pausedByUser.current[id];        // scrolling away resets the pause
        }
      });
    }, { threshold: [0.2, 0.7] });
    nodes.forEach(({ el }) => el && io.observe(el));
    return () => io.disconnect();
  }, [reels, muted, startClip]);

  // Apply the (shared) sound choice to the clip that is playing.
  useEffect(() => {
    const el = videoRefs.current[active];
    if (!el) return;
    el.muted = muted;
    if (!muted) startClip(el, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  // A tap anywhere unlocks sound for the rest of the session.
  useEffect(() => {
    if (!blocked) return undefined;
    const unlock = () => {
      setBlocked(false);
      const el = videoRefs.current[active];
      if (el) startClip(el, true);
    };
    document.addEventListener('pointerdown', unlock, { once: true });
    return () => document.removeEventListener('pointerdown', unlock);
  }, [blocked, active, startClip]);

  const like = async (post, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth(t('loginRequiredDesc'))) return;
    playSfx('success');
    const liked = !!post.liked;
    setReels((prev) => prev.map((r) => (r.id === post.id
      ? { ...r, liked: !liked, like_count: (r.like_count || 0) + (liked ? -1 : 1) }
      : r)));
    try {
      const res = await cToggleLike(post.id);
      setReels((prev) => prev.map((r) => (r.id === post.id
        ? { ...r, liked: res.liked, like_count: res.like_count }
        : r)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const togglePlay = (id) => {
    const el = videoRefs.current[id];
    if (!el) return;
    if (el.paused) {
      delete pausedByUser.current[id];
      el.play().catch(() => {});
      setPausedId((cur) => (cur === id ? null : cur));
      playSfx('play');
    } else {
      el.pause();
      pausedByUser.current[id] = true;      // remember: the viewer stopped this clip
      setPausedId(id);
      playSfx('pause');
    }
  };

  if (loading && reels.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-gray-500">
        <FiLoader className="w-5 h-5 animate-spin" /> {t('loading')}
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <p className="text-4xl">🎬</p>
        <p className="mt-3 font-bold">{t('noReels')}</p>
        <p className="text-sm text-gray-500 mt-1">{t('noReelsHint')}</p>
        <button
          type="button"
          onClick={() => navigate('/?write=1')}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-bold"
          style={{ background: BLUE }}
        >
          {t('writePost')}
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* header: how many reels + mute switch */}
      <div className="mx-auto max-w-[520px] px-3 pt-3 flex items-center justify-between">
        <h1 className="font-extrabold text-sm inline-flex items-center gap-2 dark:text-gray-100">
          🎬 {t('reelsTitle')}
          <span className="text-[11px] font-bold text-gray-400">{total}</span>
        </h1>
        <button
          type="button"
          data-sfx="toggle"
          onClick={() => setSoundOn(!soundOn)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
        >
          {muted ? <FiVolumeX className="w-4 h-4" /> : <FiVolume2 className="w-4 h-4" />}
          {muted ? t('unmute') : t('mute')}
        </button>
      </div>

      {/* the browser blocked sound autoplay → one tap turns it on everywhere */}
      {blocked && !muted && (
        <p className="mx-auto max-w-[520px] px-3 pt-2">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[11px] font-bold"
            style={{ background: BLUE }}
          >
            <FiVolume2 className="w-3.5 h-3.5" /> {t('tapForSound')}
          </span>
        </p>
      )}

      {/* the TikTok-style snap-scrolling feed: one reel per screen */}
      <div
        ref={container}
        className="mx-auto max-w-[520px] px-2 sm:px-3 py-3 space-y-3 h-[calc(100dvh-190px)] sm:h-[calc(100dvh-160px)] overflow-y-auto snap-y snap-mandatory no-scrollbar scroll-smooth"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight > el.scrollHeight - 600
              && reels.length < total && !loading) {
            const next = page + 1;
            setPage(next);
            load(next);
          }
        }}
      >

        {reels.map((post) => (
          <div
            key={post.id}
            className="relative rounded-2xl overflow-hidden bg-black snap-start"
            style={{ height: 'calc(100dvh - 210px)' }}
          >
            {/* A reel is either an uploaded clip or a video link (YouTube Shorts,
                TikTok, Facebook / Instagram Reel). An uploaded clip gets the
                player below; a link plays in the real platform player and
                autoplays the moment it scrolls into view. */}
            {post.video ? (
              <>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  data-id={post.id}
                  ref={(el) => { if (el) videoRefs.current[post.id] = el; }}
                  src={post.video.startsWith('http') ? post.video : fullUrl(post.video)}
                  poster={post.video_thumb
                    ? (post.video_thumb.startsWith('http') ? post.video_thumb : fullUrl(post.video_thumb))
                    : undefined}
                  muted={muted}
                  loop
                  playsInline
                  preload="metadata"
                  onClick={() => togglePlay(post.id)}
                  className="absolute inset-0 w-full h-full object-contain"
                />

                {/* tap to (re)play when paused */}
                {pausedId === post.id && (
                  <button
                    type="button"
                    onClick={() => togglePlay(post.id)}
                    className="absolute inset-0 flex items-center justify-center anim-fade-in"
                    aria-label="Play"
                  >
                    <span className="w-16 h-16 rounded-full bg-black/50 text-white text-2xl flex items-center justify-center">▶</span>
                  </button>
                )}
              </>
            ) : post.video_url ? (
              <div className="absolute inset-0 flex items-center justify-center px-2 py-3">
                <PostVideo url={post.video_url} title={post.title} showLabel={false} />
              </div>
            ) : null}

            {/* the profile that posted this reel — tap to open it */}
            <div className="absolute left-3 right-16 bottom-3 flex items-end gap-2.5">
              <button
                type="button"
                data-sfx="pop"
                onClick={() => navigate(`/u/${post.author?.username}`)}
                className="flex items-end gap-2.5 text-left"
              >
                <UserAvatar user={post.author} size={44} ring />
                <span className="min-w-0">
                  <span className="block text-white text-sm font-extrabold truncate">
                    {post.author?.name || post.author?.username}
                  </span>
                  <span className="block text-white/75 text-[11px] truncate">
                    @{post.author?.username}
                    {post.author?.followers_count > 0 ? ` · ${post.author.followers_count} ${t('followers')}` : ''}
                  </span>
                </span>
              </button>
            </div>

            {/* caption + category */}
            {(post.title || post.description || post.category) && (
              <div className="absolute left-3 right-16 bottom-16 text-white">
                {post.category && (
                  <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20 mb-1">
                    {post.category.icon} {lang === 'kh' ? (post.category.name_km || post.category.name) : post.category.name}
                  </span>
                )}
                {post.title && <p className="text-sm font-bold leading-snug">{post.title}</p>}
                {post.description && (
                  <p className="text-xs text-white/90 leading-snug line-clamp-3">{post.description}</p>
                )}
              </div>
            )}


            {/* action rail: like · comment · share · follow */}
            <div className="absolute right-2.5 bottom-16 flex flex-col items-center gap-3">
              <button
                type="button"
                data-sfx="success"
                onClick={(e) => like(post, e)}
                className="flex flex-col items-center text-white"
                aria-label={t('like')}
              >
                <span className={`w-10 h-10 rounded-full flex items-center justify-center ${post.liked ? 'bg-red-600' : 'bg-white/15'}`}>
                  <FiHeart className="w-5 h-5" fill={post.liked ? '#fff' : 'none'} />
                </span>
                <span className="text-[10px] font-bold mt-1">{post.like_count || 0}</span>
              </button>

              <button
                type="button"
                data-sfx="tap"
                onClick={() => setCommentsFor(post)}
                className="flex flex-col items-center text-white"
                aria-label={t('comments')}
              >
                <span className="w-10 h-10 rounded-full flex items-center justify-center bg-white/15">
                  <FiMessageCircle className="w-5 h-5" />
                </span>
                <span className="text-[10px] font-bold mt-1">{post.comment_count || 0}</span>
              </button>

              <span className="[&_button]:text-white [&_button]:bg-white/15 [&_button]:rounded-full">
                <ShareMenu post={post} />
              </span>

              {user && post.author?.id !== user.id && (
                <span className="scale-90">
                  <FollowButton user={{ ...post.author, is_following: post.following_author }} />
                </span>
              )}
            </div>

            {/* duration + open the full post */}
            <div className="absolute top-3 left-3 flex items-center gap-2">
              {post.video_duration > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/55 text-white">
                  {formatTime(post.video_duration)}
                </span>
              )}
              <Link
                to={`/post/${post.id}`}
                data-sfx="tap"
                className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20 text-white"
              >
                {t('openPost')}
              </Link>
            </div>
          </div>
        ))}

        {loading && reels.length > 0 && (
          <p className="text-center text-xs text-gray-400 py-3 inline-flex items-center gap-1.5 w-full justify-center">
            <FiLoader className="w-3.5 h-3.5 animate-spin" /> {t('loading')}
          </p>
        )}
        {reels.length >= total && reels.length > 2 && (
          <p className="text-center text-xs text-gray-400 py-3 inline-flex items-center gap-1.5 w-full justify-center">
            <FiChevronDown className="w-3.5 h-3.5" /> {t('noMoreReels')}
          </p>
        )}
      </div>

      {/* comments of the active reel — the header stays pinned while scrolling */}
      {commentsFor && (
        <div className="fixed inset-0 z-[900] flex items-end sm:items-center justify-center" role="dialog">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm anim-fade-in" onClick={() => setCommentsFor(null)} />
          <div className="relative w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl bg-white dark:bg-gray-900 anim-slide-up flex flex-col max-h-[85dvh]">
            {/* pinned header with a big, always-visible close button */}
            <div className="sticky top-0 z-10 rounded-t-3xl bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
              <button
                type="button"
                data-sfx="tap"
                onClick={() => setCommentsFor(null)}
                className="w-full pt-2 pb-1 flex justify-center"
                aria-label={t('close')}
              >
                <span className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
              </button>
              <div className="flex items-center justify-between px-4 pb-2">
                <h3 className="font-extrabold text-sm dark:text-gray-100">💬 {t('comments')}</h3>
                <button
                  type="button"
                  data-sfx="tap"
                  onClick={() => setCommentsFor(null)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                >
                  <FiX className="w-4 h-4" /> {t('close')}
                </button>
              </div>
            </div>

            <div className="overflow-y-auto px-4 pb-4 scroll-smooth">
              <CommentList
                postId={commentsFor.id}
                postOwnerId={commentsFor.user_id}
                canComment={commentsFor.allow_comments !== false}
                onCount={(delta) => setReels((prev) => prev.map((r) => (r.id === commentsFor.id
                  ? { ...r, comment_count: Math.max(0, (r.comment_count || 0) + delta) }
                  : r)))}
              />
            </div>
          </div>
        </div>
      )}

      <p className="mx-auto max-w-[520px] px-3 pb-4 text-center text-[11px] text-gray-400">
        <FiUserPlus className="w-3 h-3 inline" /> {t('reelsHint')}
      </p>
    </div>
  );
}

